import { Button } from "@mui/material";

import Page from "~/components/Page";
import TableControls from "~/components/TableControls";
import { useTrans } from '~/hooks/useTrans';
import { apiGetAdminUsers, apiResetAdminUser } from "~/providers/api";
import { IUserAdmin } from "~/providers/interfaces";
import ErrorInfo from '~/components/ErrorInfo';
import { myUseState } from "~/hooks/myUseState";
import { useLang } from "~/hooks/useLang";
import AdminApiUsersTable, { TApiUserTableName } from "~/tables/AdminApiUsersTable";
import AdminUserDataModal from "~/modals/AdminUserDataModal";
import RemoveDialog from "~/modals/RemoveDialog";
import AdminUserCreateModal from "~/modals/AdminUserCreateModal";

import "./style.less";

interface IState {
	loading: boolean;
	items: Array<IUserAdmin>;
	errorMsg: string;
	editUserData: IUserAdmin;
	removeUserData: IUserAdmin;
	addUser: boolean;
	isDirty: boolean;
}

const INIT_STATE: IState = {
	loading: true,
	items: [],
	errorMsg: "",
	editUserData: null,
	removeUserData: null,
	addUser: false,
	isDirty: false,
};

export default function AdminApiUsersPage() {
	const { trans } = useTrans();
	const { lang } = useLang();
	//const { params }: IOutlet = useOutletContext();
	const { state, updateState } = myUseState<IState>({
		...INIT_STATE,
	});

	async function loadData() {
		updateState({
			loading: true,
			items: [],
			errorMsg: "",
		});

		const usersData = await apiGetAdminUsers(lang);

		updateState({
			loading: false,
			...usersData.noErrors
				? {
					items: usersData.data,
				}
				: {},
			errorMsg: usersData.errors.join(", "),
		});
	}

	function addUser() {
		updateState({
			addUser: true,
		});
	}

	function onApiUsersTableSignal(name: TApiUserTableName, userData: IUserAdmin) {
		switch (name) {
			case "edit":
				updateState({
					editUserData: userData,
				});
				break;

			case "delete":
				updateState({
					removeUserData: userData,
				});
				break;

			default:
		}
	}

	function saveNotUserData(newData: IUserAdmin) {
		const curItems = state.items.slice();
		const filtered = curItems.filter(item => item.email === newData.email);

		if (filtered.length !== 0) {
			Object.assign(filtered[0], newData);
		}

		updateState({
			editUserData: null,
			items: curItems,
			isDirty: false,
		});
	}

	async function onRemoveUserData(resolve: () => void, reject: (error: string) => void) {
		const resetData = await apiResetAdminUser(lang, state.removeUserData.id);

		if (resetData.noErrors) {
			const curUsers = state.items.filter(item => item.email !== state.removeUserData.email);

			updateState({
				items: curUsers,
			});
			resolve();
		} else {
			reject(resetData.errors.join(", "));
		}
	}

	function onAddUser(newUser: IUserAdmin) {
		const items = state.items.slice();

		items.unshift(newUser);
		updateState({
			isDirty: false,
			addUser: false,
			items,
		});
	}

	function onPageLoad(isAuthorized: boolean) {
		if (isAuthorized) {
			loadData();
		}
	}

	return <Page title={trans("menu.adminApiUsers")} breadcrumbs={[trans("administration"), trans("menu.adminApiUsers")]} checkInternalRole="salesman" onLoad={onPageLoad} isDirty={state.isDirty} seoTitleTrans="seo.title.admin.apiUsers">
		<TableControls className="adminApiUsers__tableControls">
			<Button color="success" variant="contained" onClick={addUser}>
				{ trans("adminApiUsersPage.addUser") }
			</Button>
		</TableControls>
		<AdminApiUsersTable items={state.items} loading={state.loading} onSignal={onApiUsersTableSignal} />
		{ state.errorMsg && <ErrorInfo msg={state.errorMsg} className="margin-top-large" /> }
		{ state.editUserData && <AdminUserDataModal userData={state.editUserData} onClose={() => updateState({ editUserData: null, isDirty: false })} onSave={saveNotUserData} onChange={isDirty => updateState({ isDirty })} /> }
		{ state.removeUserData && <RemoveDialog title={trans("removeUserDialog.title")} desc={trans("removeUserDialog.desc", { user: state.removeUserData.email })} onClose={() => updateState({ removeUserData: null })}
			onRemove={onRemoveUserData} /> }
		{ state.addUser && <AdminUserCreateModal onChange={isDirty => updateState({ isDirty })} onSave={onAddUser} onClose={() => updateState({ isDirty: false, addUser: false })} /> }
	</Page>;
}
